import React, { useState } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { UserRound } from 'lucide-react'
import { resumeHandoffForConversation } from '../../api/conversations'

// Shown above the thread while a conversation is handed off to staff: the
// customer is waiting on a person and the bot is not replying. Separate from
// the AI on/off toggle — resuming here never changes that toggle.
//
// Render with key={userId}: the in-flight state belongs to one conversation
// and must not carry over when staff switch threads mid-request.
export default function HandoffBanner({
  tenantId,
  userId,
  handedOff,
  onResumed,
  sentryAction = 'resume_handoff_desktop',
}) {
  const { t } = useTranslation()
  const [resuming, setResuming] = useState(false)

  if (!handedOff) return null

  const canResume = Boolean(tenantId && userId) && !resuming

  async function handleResume() {
    if (!canResume) return
    setResuming(true)
    try {
      await resumeHandoffForConversation(tenantId, userId)
      await onResumed?.()
      toast.success(t('inbox.resumeBotDone', { defaultValue: 'The chatbot is replying in this conversation again.' }))
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: sentryAction },
        extra: { tenantId, userId },
      })
      toast.error(t('inbox.resumeBotFailed', {
        defaultValue: 'Could not resume chatbot replies. Please try again.',
      }))
    } finally {
      setResuming(false)
    }
  }

  return (
    <div
      style={{
        background: '#eff6ff',
        borderBottom: '0.5px solid #bfdbfe',
        padding: '8px 18px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
      }}
    >
      <UserRound size={14} color="#1d4ed8" style={{ flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: '#1e3a8a', flex: 1, minWidth: 0 }}>
        {t('inbox.handoffBanner', {
          defaultValue: 'This customer is waiting for a team member. The chatbot has paused its replies in this conversation.',
        })}
      </span>
      <button
        type="button"
        onClick={handleResume}
        disabled={!canResume}
        style={{
          fontSize: 12,
          padding: '5px 12px',
          borderRadius: 8,
          border: 'none',
          fontWeight: 500,
          background: '#1d4ed8',
          color: 'white',
          whiteSpace: 'nowrap',
          cursor: canResume ? 'pointer' : 'not-allowed',
          opacity: canResume ? 1 : 0.7,
        }}
      >
        {t('inbox.resumeBot', { defaultValue: 'Resume chatbot replies' })}
      </button>
    </div>
  )
}
